/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  FetchReportProperties,
  FilterReportInterFace,
  ReportInitialProperties,
  RequestReportInterFace,
} from '../../interface/reportenergyPoint.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import report from '../../utils/api/reports';

interface AccountParameters {
  accountCustomId?: string;
}

// a thunk to fetch stations
export const fetchEnergyReportPoint = createAsyncThunk<FetchReportProperties, RequestReportInterFace>(
  'reports/fetchEnergyReportPoint',
  async (data: RequestReportInterFace, { dispatch }) => {
    try {
      const response = await report.fetchEnergyReportListPoint(removeEmptyKeys(data));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const fetchListFilters = createAsyncThunk<CommonFilterProperties[], AccountParameters>(
  'reports/fetchListEnergyPointFilters',
  async (data: AccountParameters, { dispatch }) => {
    try {
      const response = await report.fetchListPointFilters(data);
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

const stationFilter: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ReportInitialProperties = {
  reports: [],
  loading: false,
  page: 1,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
  filters: stationFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
  isEnergyPointReportUpdate: false, // Add this line to track report update state
};

// Create the slice
export const reportsEnergyPointSlice = createSlice({
  name: 'reportsEnergyPoint',
  initialState,
  reducers: {
    /**
     * Set User filter data function
     * @param state
     * @param action
     */
    setReportFilterData: (
      state: ReportInitialProperties,
      action: PayloadAction<FilterReportInterFace>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Set User filter data function
     * @param state
     * @param action
     */
    setReportFiltersData: (
      state: ReportInitialProperties,
      action: PayloadAction<CommonFilterProperties[]>,
    ) => {
      state.filters = action.payload;
    },

        /**
     * Explicitly set the energy station report update flag
     * @param state
     * @param action
     */
    setEnergyPointReportUpdate: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isEnergyPointReportUpdate = action.payload;
    },
    /**
     * Function that reset slice to initial state
     * @param state
     */
    resetReportSliceToInitialState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update station management filters
     * @param state
     * @param action
     */
    updateReportFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      console.log(payload);
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
        // state.filters = UpdateCommonFilter(state.filters, action.payload);
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchEnergyReportPoint.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEnergyReportPoint.fulfilled, (state, action) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.reports = action.payload.results;
      })
      .addCase(fetchEnergyReportPoint.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const {
  setReportFilterData,
  setReportFiltersData,
  resetReportSliceToInitialState,
  updateReportFilters,
  setSortData,
  setEnergyPointReportUpdate,
} = reportsEnergyPointSlice.actions;

// Export the reducer
export default reportsEnergyPointSlice.reducer;
